import { ChevronDown } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useState, type CSSProperties } from "react";
import { Link, useLocation } from "react-router";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { formatBadgeCount } from "../../dashboard/desk/format";
import { ICON_STROKE, useNavLabel } from "./navHelpers";
import {
  activeLeafId,
  isItemActive,
  leafHref,
  type DeskNavItem,
} from "./navConfig";

/** Wraps `children` in a right-side tooltip, only in the collapsed rail. */
export const RailTooltip = ({
  label,
  enabled,
  children,
}: {
  label: string;
  enabled: boolean;
  children: React.ReactElement;
}) =>
  enabled ? (
    <Tooltip>
      <TooltipTrigger render={children} />
      <TooltipContent side="right" sideOffset={10}>
        {label}
      </TooltipContent>
    </Tooltip>
  ) : (
    children
  );

const ItemIcon = ({ item, active }: { item: DeskNavItem; active: boolean }) => {
  const Icon = item.icon;
  const filled = active && item.fillWhenActive;
  return (
    <span className="desk-nav__icon" aria-hidden>
      <Icon
        className="size-4"
        strokeWidth={active ? 1.75 : ICON_STROKE}
        fill={filled ? "currentColor" : "none"}
        data-filled={filled ? "true" : undefined}
      />
    </span>
  );
};

const Badge = ({ count, collapsed }: { count: number; collapsed: boolean }) => {
  const text = formatBadgeCount(count);
  if (!text) return null;
  return collapsed ? (
    <span className="desk-nav__dot" aria-hidden />
  ) : (
    <span className="desk-nav__badge">{text}</span>
  );
};

interface NavRowProps {
  item: DeskNavItem;
  collapsed: boolean;
  badgeCount: number;
}

const NavLinkItem = ({ item, collapsed, badgeCount }: NavRowProps) => {
  const { pathname } = useLocation();
  const labelOf = useNavLabel();
  const active = isItemActive(item, pathname);
  const label = labelOf(item.label);
  return (
    <li>
      <RailTooltip label={label} enabled={collapsed}>
        <Link
          to={item.to}
          className="desk-nav__item desk-press"
          data-active={active ? "true" : undefined}
          aria-current={active ? "page" : undefined}
          aria-label={collapsed ? label : undefined}
        >
          <ItemIcon item={item} active={active} />
          <span className="desk-nav__label">{label}</span>
          <Badge count={badgeCount} collapsed={collapsed} />
        </Link>
      </RailTooltip>
    </li>
  );
};

/** Collapsed rail: a group icon opens its sub-items in a flyout. */
const NavGroupFlyout = ({
  item,
  badgeCount,
}: Omit<NavRowProps, "collapsed">) => {
  const { pathname, search } = useLocation();
  const labelOf = useNavLabel();
  const children = item.children ?? [];
  const active = isItemActive(item, pathname);
  const activeLeaf = activeLeafId(children, pathname, search);
  const label = labelOf(item.label);
  return (
    <li>
      <DropdownMenu>
        <DropdownMenuTrigger
          className="desk-nav__item desk-press"
          data-active={active ? "true" : undefined}
          aria-label={label}
          title={label}
        >
          <ItemIcon item={item} active={active} />
          <span className="desk-nav__label">{label}</span>
          <Badge count={badgeCount} collapsed />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          sideOffset={12}
          className="w-52"
        >
          <DropdownMenuGroup>
            <DropdownMenuLabel>{label}</DropdownMenuLabel>
            {children.map((leaf) => (
              <DropdownMenuItem
                key={leaf.id}
                render={<Link to={leafHref(leaf)} />}
                data-active={leaf.id === activeLeaf ? "true" : undefined}
                className="data-[active=true]:font-medium"
              >
                {labelOf(leaf.label)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
};

const NavGroup = ({ item, badgeCount }: Omit<NavRowProps, "collapsed">) => {
  const { pathname, search } = useLocation();
  const labelOf = useNavLabel();
  const children = item.children ?? [];
  const containsActive = isItemActive(item, pathname);
  const activeLeaf = activeLeafId(children, pathname, search);
  const [open, setOpen] = useState(containsActive);

  // Auto-open when navigation lands on one of the group's routes.
  useEffect(() => {
    if (containsActive) setOpen(true);
  }, [containsActive]);

  const panelId = `desk-nav-${item.id}`;
  return (
    <li className="desk-nav__group" data-open={open ? "true" : undefined}>
      <button
        type="button"
        className="desk-nav__item desk-press"
        data-current={containsActive ? "true" : undefined}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <ItemIcon item={item} active={false} />
        <span className="desk-nav__label">{labelOf(item.label)}</span>
        <Badge count={badgeCount} collapsed={false} />
        <ChevronDown
          aria-hidden
          className="desk-nav__chevron size-3.5"
          strokeWidth={ICON_STROKE}
        />
      </button>
      <div className="desk-nav__collapse" id={panelId} inert={!open}>
        <ul className="desk-nav__sub">
          {children.map((leaf, index) => {
            const active = leaf.id === activeLeaf;
            return (
              <li
                key={leaf.id}
                className="desk-nav__subrow"
                style={{ "--i": index } as CSSProperties}
              >
                <Link
                  to={leafHref(leaf)}
                  className="desk-nav__subitem"
                  data-active={active ? "true" : undefined}
                  aria-current={active ? "page" : undefined}
                >
                  {labelOf(leaf.label)}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
};

export const DeskNav = ({
  items,
  collapsed,
  badges,
}: {
  items: DeskNavItem[];
  collapsed: boolean;
  badges: Partial<Record<NonNullable<DeskNavItem["badge"]>, number>>;
}) => {
  const translate = useTranslate();
  return (
    <nav aria-label={translate("crm.navigation.label")} className="desk-nav">
      <ul className="desk-nav__list">
        {items.map((item) => {
          const badgeCount = item.badge ? (badges[item.badge] ?? 0) : 0;
          if (!item.children) {
            return (
              <NavLinkItem
                key={item.id}
                item={item}
                collapsed={collapsed}
                badgeCount={badgeCount}
              />
            );
          }
          return collapsed ? (
            <NavGroupFlyout key={item.id} item={item} badgeCount={badgeCount} />
          ) : (
            <NavGroup key={item.id} item={item} badgeCount={badgeCount} />
          );
        })}
      </ul>
    </nav>
  );
};
